import DashboardLayout from "@/components/DashboardLayout";
import { PageHeader } from "@/components/PageHeader";
import { ResourceCard } from "@/components/ResourceCard";
import { Button } from "@/components/ui/button";
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle, DialogTrigger } from "@/components/ui/dialog";
import { Empty, EmptyContent, EmptyDescription, EmptyHeader, EmptyMedia, EmptyTitle } from "@/components/ui/empty";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Spinner } from "@/components/ui/spinner";
import { Switch } from "@/components/ui/switch";
import { Textarea } from "@/components/ui/textarea";
import { trackEvent } from "@/lib/analytics";
import { trpc } from "@/lib/trpc";
import { Activity, Clock, Key, Plus } from "lucide-react";
import { useState } from "react";
import { toast } from "sonner";
import { useLocation } from "wouter";

export default function Secrets() {
  const [, setLocation] = useLocation();
  const [isCreateOpen, setIsCreateOpen] = useState(false);
  const [name, setName] = useState("");
  const [description, setDescription] = useState("");
  const [value, setValue] = useState("");
  const [autoWhitelistFirstRequest, setAutoWhitelistFirstRequest] = useState(false);

  const { data: secrets, isLoading, refetch } = trpc.secrets.list.useQuery();
  const createMutation = trpc.secrets.create.useMutation({
    onSuccess: () => {
      trackEvent("secret_created");
      toast.success("Secret created successfully");
      setIsCreateOpen(false);
      setName("");
      setDescription("");
      setValue("");
      setAutoWhitelistFirstRequest(false);
      refetch();
    },
    onError: (error) => {
      toast.error(error.message);
    },
  });

  const handleCreate = () => {
    if (!name || !value) {
      toast.error("Name and value are required");
      return;
    }

    createMutation.mutate({
      name,
      description: description || undefined,
      value,
      autoWhitelistFirstRequest,
    });
  };

  return (
    <DashboardLayout>
      <div className="space-y-6">
        <PageHeader
          title="Secrets"
          subtitle="Manage your API keys and credentials"
          action={
            <Dialog open={isCreateOpen} onOpenChange={setIsCreateOpen}>
              <DialogTrigger asChild>
                <Button>
                  <Plus className="w-4 h-4 mr-2" />
                  New Secret
                </Button>
              </DialogTrigger>
              <DialogContent className="max-h-[90vh] overflow-y-auto">
                <DialogHeader>
                  <DialogTitle>Create New Secret</DialogTitle>
                  <DialogDescription>
                    Add a new secret to store securely. All secrets are encrypted at rest.
                  </DialogDescription>
                </DialogHeader>
                <div className="space-y-4 py-4">
                  <div className="space-y-2">
                    <Label htmlFor="name">Name *</Label>
                    <Input
                      id="name"
                      placeholder="e.g., OpenAI API Key"
                      value={name}
                      onChange={(e) => setName(e.target.value)}
                    />
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="description">Description</Label>
                    <Textarea
                      id="description"
                      placeholder="Optional description of this secret"
                      value={description}
                      onChange={(e) => setDescription(e.target.value)}
                    />
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="value">Secret Value *</Label>
                    <Textarea
                      id="value"
                      placeholder="Paste your API key or secret here"
                      value={value}
                      onChange={(e) => setValue(e.target.value)}
                      className="font-mono"
                    />
                  </div>
                  <div className="flex items-center justify-between space-x-2">
                    <div className="space-y-0.5">
                      <Label htmlFor="auto-whitelist">Auto-whitelist First Request</Label>
                      <p className="text-sm text-muted-foreground">
                        Automatically whitelist the IP address of the first access
                      </p>
                    </div>
                    <Switch
                      id="auto-whitelist"
                      checked={autoWhitelistFirstRequest}
                      onCheckedChange={setAutoWhitelistFirstRequest}
                    />
                  </div>
                </div>
                <DialogFooter>
                  <Button variant="outline" onClick={() => setIsCreateOpen(false)}>
                    Cancel
                  </Button>
                  <Button onClick={handleCreate} disabled={createMutation.isPending}>
                    {createMutation.isPending ? "Creating..." : "Create Secret"}
                  </Button>
                </DialogFooter>
              </DialogContent>
            </Dialog>
          }
        />

        {isLoading ? (
          <div className="flex justify-center py-12">
            <Spinner className="size-8 text-primary" />
          </div>
        ) : secrets && secrets.length > 0 ? (
          <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-3">
            {secrets.map((secret) => (
              <ResourceCard
                key={secret.id}
                icon={Key}
                title={secret.name}
                description={secret.description ?? undefined}
                metadata={[
                  {
                    icon: Activity,
                    label: `${secret.activeTokenCount || 0} active token${secret.activeTokenCount !== 1 ? "s" : ""}`,
                  },
                  ...(secret.lastUsedAt
                    ? [{ icon: Clock, label: `Last used ${new Date(secret.lastUsedAt).toLocaleDateString()}` }]
                    : []),
                ]}
                onClick={() => setLocation(`/secrets/${secret.id}`)}
              />
            ))}
          </div>
        ) : (
          <Empty>
            <EmptyHeader>
              <EmptyMedia variant="icon">
                <Key />
              </EmptyMedia>
              <EmptyTitle>No secrets yet</EmptyTitle>
              <EmptyDescription>
                Create your first secret to get started with secure credential management.
              </EmptyDescription>
            </EmptyHeader>
            <EmptyContent>
              <Button onClick={() => setIsCreateOpen(true)}>
                <Plus className="w-4 h-4 mr-2" />
                Create Secret
              </Button>
            </EmptyContent>
          </Empty>
        )}
      </div>
    </DashboardLayout>
  );
}
